import { useEffect, useRef, useState } from "react";

import {
  ArrowUpRight,
  Check,
  Sparkles,
  Zap,
  Layers3,
  ShieldCheck,
} from "lucide-react";

import solution from "../../../Assets/Images/solution.png";
import person1 from "../../../Assets/Images/person1.png";
import person2 from "../../../Assets/Images/person2.png";
import person3 from "../../../Assets/Images/person3.png";
import person4 from "../../../Assets/Images/person4.png";

import GradientBlinds from "../../../components/animations/GradientBlinds.jsx";

import { useNavigate } from "react-router-dom";

// ============================================================
// MAIN SOLUTION COMPONENT
// ============================================================

const Solution = () => {
  const containerRef = useRef(null);
  const leftCardRef = useRef(null);
  const imageRef = useRef(null);
  const rightCardRef = useRef(null);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );

  const navigate = useNavigate();

  // ==========================================================
  // IMAGE PARALLAX
  // ==========================================================

  useEffect(() => {
    const cardEl = leftCardRef.current;
    const imageEl = imageRef.current;

    if (!cardEl || !imageEl) return;

    let ticking = false;
    let animationFrameId;

    const handleScroll = () => {
      if (ticking) return;

      ticking = true;

      animationFrameId = requestAnimationFrame(() => {
        const rect = cardEl.getBoundingClientRect();
        const cardCenter = rect.top + rect.height / 2;
        const windowCenter = window.innerHeight / 2;

        const distance = cardCenter - windowCenter;
        const maxOffset = 35;

        const offset = Math.max(
          Math.min(distance * 0.08, maxOffset),
          -maxOffset
        );

        imageEl.style.transform = `translate3d(0, ${offset}px, 0)`;

        ticking = false;
      });
    };

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    window.addEventListener("resize", handleScroll);

    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);

      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  // ==========================================================
  // RESPONSIVE BREAKPOINT
  // ==========================================================

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  // ==========================================================
  // NAVIGATION
  // ==========================================================

  const handleLearnMore = () => {
    navigate("/about");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // ==========================================================
  // TEAM AVATARS
  // ==========================================================

  const people = [person1, person2, person3, person4];

  // ==========================================================
  // SOLUTION FEATURES
  // ==========================================================

  const solutionFeatures = [
    {
      icon: Layers3,
      title: "Tailored solutions",
      description: "Built around your business goals.",
    },
    {
      icon: Zap,
      title: "Modern technology",
      description: "Fast, scalable and future-ready.",
    },
    {
      icon: ShieldCheck,
      title: "Reliable delivery",
      description: "Focused on quality and long-term value.",
    },
  ];

  return (
    <section
      ref={containerRef}
      className="
        relative
        overflow-hidden
        theme-bg-primary
        px-3
        py-5
        sm:px-5
        sm:py-6
        md:px-7
        md:py-8
        lg:px-10
        lg:py-9
        xl:px-12
        xl:py-10
      "
    >
      {/* ========================================================
          BACKGROUND ATMOSPHERE
      ========================================================= */}

      <div
        className="
          pointer-events-none
          absolute
          left-1/2
          top-1/2
          h-[20rem]
          w-[20rem]
          -translate-x-1/2
          -translate-y-1/2
          rounded-full
          bg-[var(--brand-primary)]/[0.025]
          blur-[100px]
          sm:h-[25rem]
          sm:w-[25rem]
          md:h-[30rem]
          md:w-[30rem]
          lg:h-[38rem]
          lg:w-[38rem]
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          right-[-10rem]
          top-[-8rem]
          h-[20rem]
          w-[20rem]
          rounded-full
          bg-[var(--brand-primary)]/[0.025]
          blur-[100px]
          md:h-[25rem]
          md:w-[25rem]
        "
      />

      {/* ========================================================
          MAIN WRAPPER
      ========================================================= */}

      <div
        className="
          relative
          z-10
          mx-auto
          w-full
          max-w-[1450px]
        "
      >
        {/* ======================================================
            GRID
        ======================================================= */}

        <div
          className="
            grid
            grid-cols-1
            gap-3
            sm:gap-4
            md:gap-5
            lg:grid-cols-2
            lg:gap-5
            xl:gap-6
          "
        >
          {/* ====================================================
              LEFT IMAGE CARD
          ===================================================== */}

          <div
            ref={leftCardRef}
            className="
              group
              relative
              h-[235px]
              overflow-hidden
              rounded-[17px]
              border
              theme-border
              theme-card
              shadow-[0_18px_55px_var(--shadow-color)]
              sm:h-[285px]
              sm:rounded-[19px]
              md:h-[330px]
              lg:h-[390px]
              lg:rounded-[21px]
              xl:h-[420px]
            "
          >
            {/* ==================================================
                IMAGE
            =================================================== */}

            <img
              ref={imageRef}
              src={solution}
              alt="Team working together"
              loading="lazy"
              className="
                absolute
                left-0
                top-0
                h-[130%]
                w-full
                object-cover
              "
              style={{
                top: "-15%",
                transition: "transform 0.08s linear",
                willChange: "transform",
              }}
            />

            {/* ==================================================
                IMAGE OVERLAY
            =================================================== */}

            <div
              className="
                pointer-events-none
                absolute
                inset-0
                bg-gradient-to-t
                from-black/80
                via-black/15
                to-black/5
              "
            />

            <div
              className="
                pointer-events-none
                absolute
                -bottom-24
                -left-20
                h-60
                w-60
                rounded-full
                bg-[var(--brand-primary)]/[0.12]
                blur-[80px]
              "
            />

            {/* ==================================================
                TOP LABEL
            =================================================== */}

            <div
              className="
                absolute
                left-3
                top-3
                sm:left-4
                sm:top-4
                md:left-5
                md:top-5
              "
            >
              <div
                className="
                  inline-flex
                  items-center
                  gap-1.5
                  rounded-full
                  border
                  border-white/[0.12]
                  bg-black/35
                  px-2.5
                  py-1
                  backdrop-blur-md
                  sm:gap-2
                  sm:px-3
                  sm:py-1.5
                "
              >
                <span
                  className="
                    h-1
                    w-1
                    rounded-full
                    brand-bg
                    shadow-[0_0_10px_rgba(255,128,0,0.8)]
                    sm:h-1.5
                    sm:w-1.5
                  "
                />

                <span
                  className="
                    font-saira
                    text-[7.5px]
                    font-semibold
                    uppercase
                    tracking-[0.18em]
                    text-white/75
                    sm:text-[8.5px]
                    sm:tracking-[0.2em]
                  "
                >
                  Digital Solutions
                </span>
              </div>
            </div>

            {/* ==================================================
                IMAGE BOTTOM CONTENT
            =================================================== */}

            <div
              className="
                absolute
                bottom-3
                left-3
                right-3
                sm:bottom-4
                sm:left-4
                sm:right-4
                md:bottom-5
                md:left-5
                md:right-5
              "
            >
              <h3
                className="
                  font-saira
                  text-[1.05rem]
                  font-semibold
                  tracking-[-0.02em]
                  text-white
                  sm:text-[1.2rem]
                  md:text-[1.35rem]
                "
              >
                Turning ideas into digital reality.
              </h3>

              <p
                className="
                  mt-1
                  max-w-[430px]
                  font-saira
                  text-[9.5px]
                  font-medium
                  leading-relaxed
                  text-white/60
                  sm:text-[10.5px]
                  md:text-[11.5px]
                "
              >
                We transform business challenges into practical,
                scalable and meaningful digital experiences.
              </p>
            </div>

            {/* ==================================================
                IMAGE BORDER ACCENT
            =================================================== */}

            <div
              className="
                pointer-events-none
                absolute
                inset-0
                rounded-[17px]
                border
                border-transparent
                transition-all
                duration-500
                group-hover:border-[var(--brand-primary)]/25
              "
            />
          </div>

          {/* ====================================================
              RIGHT PREMIUM CONTENT CARD
          ===================================================== */}

          <div
            ref={rightCardRef}
            className="
              relative
              min-h-[390px]
              overflow-hidden
              rounded-[17px]
              border
              theme-border
              theme-card
              p-4
              shadow-[0_18px_55px_var(--shadow-color)]
              sm:min-h-[410px]
              sm:rounded-[19px]
              sm:p-5
              md:min-h-[430px]
              md:p-6
              lg:min-h-[390px]
              lg:rounded-[21px]
              lg:p-7
              xl:min-h-[420px]
              xl:p-8
            "
          >
            {/* ==================================================
                GRADIENT BLINDS
            =================================================== */}

            {!isMobile && (
              <GradientBlinds
                gradientColors={[
                  "#E67000",
                  "#FF8C21",
                  "#B35700",
                ]}
                blindCount={12}
                blindMinWidth={30}
                minBlindCount={8}
                noise={0.08}
                spotlightRadius={0.4}
                spotlightOpacity={1}
                distortAmount={2}
                className="pointer-events-none"
                interactionOnHover={true}
                initialSpotlightX={0.9}
                initialSpotlightY={0.5}
              />
            )}

            {/* ==================================================
                PREMIUM OVERLAYS
            =================================================== */}

            <div
              className="
                pointer-events-none
                absolute
                inset-0
                bg-gradient-to-br
                from-[var(--brand-primary)]/[0.11]
                via-black/[0.62]
                to-black/[0.94]
              "
            />

            <div
              className="
                pointer-events-none
                absolute
                inset-0
                bg-[radial-gradient(circle_at_88%_8%,rgba(255,128,0,0.2),transparent_38%)]
              "
            />

            <div
              className="
                pointer-events-none
                absolute
                bottom-[-5rem]
                left-[-3rem]
                h-44
                w-64
                rounded-full
                bg-[var(--brand-primary)]/[0.07]
                blur-[80px]
              "
            />

            {/* ==================================================
                CONTENT
            =================================================== */}

            <div
              className="
                relative
                z-10
                flex
                h-full
                flex-col
              "
            >
              {/* =================================================
                  TOP TRUST ROW
              ================================================== */}

              <div
                className="
                  flex
                  items-center
                  justify-between
                  gap-3
                "
              >
                {/* Eyebrow */}

                <div>
                  <span
                    className="
                      inline-flex
                      items-center
                      gap-1.5
                      rounded-full
                      border
                      border-[var(--brand-primary)]/25
                      bg-[var(--brand-primary)]/[0.08]
                      px-2.5
                      py-1
                      font-saira
                      text-[7.5px]
                      font-semibold
                      uppercase
                      tracking-[0.18em]
                      brand-color
                      sm:px-3
                      sm:py-1.5
                      sm:text-[8.5px]
                    "
                  >
                    <Sparkles
                      size={10}
                      strokeWidth={2}
                    />

                    Our approach
                  </span>
                </div>

                {/* Team avatars */}

                <div className="flex items-center">
                  <div className="flex -space-x-1.5 sm:-space-x-2">
                    {people.map((img, index) => (
                      <img
                        key={index}
                        src={img}
                        alt={`Team member ${index + 1}`}
                        className="
                          h-6
                          w-6
                          rounded-full
                          border
                          border-black
                          bg-zinc-800
                          object-cover
                          sm:h-7
                          sm:w-7
                          md:h-8
                          md:w-8
                        "
                      />
                    ))}
                  </div>

                  <span
                    className="
                      ml-2
                      hidden
                      font-saira
                      text-[7.5px]
                      font-medium
                      text-white/45
                      sm:block
                      sm:text-[8.5px]
                    "
                  >
                    Built together
                  </span>
                </div>
              </div>

              {/* =================================================
                  MAIN HEADING
              ================================================== */}

              <h2
                className="
                  mt-4
                  max-w-[620px]
                  font-saira
                  text-[1.9rem]
                  font-semibold
                  leading-[1.05]
                  tracking-[-0.045em]
                  text-white
                  sm:mt-5
                  sm:text-[2.2rem]
                  md:text-[2.6rem]
                  lg:mt-4
                  lg:text-[2.8rem]
                  xl:text-[3.15rem]
                "
              >
                Smart technology.
                <br />

                <span className="brand-color">
                  Built around your business.
                </span>
              </h2>

              {/* =================================================
                  DESCRIPTION
              ================================================== */}

              <p
                className="
                  mt-3
                  max-w-[560px]
                  font-saira
                  text-[10.5px]
                  font-medium
                  leading-[1.65]
                  text-white/55
                  sm:mt-3.5
                  sm:text-[11.5px]
                  md:text-[12.5px]
                  lg:text-[13px]
                "
              >
                From software and web development to communication
                and compliance solutions, we create technology that
                solves real problems and supports sustainable growth.
              </p>

              {/* =================================================
                  FEATURE GRID
              ================================================== */}

              <div
                className="
                  mt-4
                  grid
                  grid-cols-1
                  gap-2
                  sm:mt-5
                  sm:grid-cols-3
                  sm:gap-2
                  md:mt-6
                  md:gap-2.5
                "
              >
                {solutionFeatures.map((feature, index) => {
                  const Icon = feature.icon;

                  return (
                    <div
                      key={index}
                      className="
                        group/feature
                        rounded-[10px]
                        border
                        theme-border
                        bg-black/25
                        p-2.5
                        backdrop-blur-md
                        transition-all
                        duration-300
                        hover:border-[var(--brand-primary)]/25
                        hover:bg-[var(--brand-primary)]/[0.05]
                        sm:p-3
                      "
                    >
                      <div
                        className="
                          flex
                          items-start
                          gap-2
                        "
                      >
                        <span
                          className="
                            flex
                            h-6
                            w-6
                            shrink-0
                            items-center
                            justify-center
                            rounded-lg
                            border
                            border-[var(--brand-primary)]/20
                            bg-[var(--brand-primary)]/[0.08]
                            brand-color
                            transition-all
                            duration-300
                            group-hover/feature:bg-[var(--brand-primary)]
                            group-hover/feature:text-black
                            sm:h-7
                            sm:w-7
                          "
                        >
                          <Icon
                            size={12}
                            strokeWidth={2}
                          />
                        </span>

                        <div className="min-w-0">
                          <h3
                            className="
                              font-saira
                              text-[9.5px]
                              font-semibold
                              leading-tight
                              text-white
                              sm:text-[10.5px]
                            "
                          >
                            {feature.title}
                          </h3>

                          <p
                            className="
                              mt-0.5
                              font-saira
                              text-[7.5px]
                              leading-[1.4]
                              text-white/40
                              sm:text-[8.5px]
                            "
                          >
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* =================================================
                  BOTTOM CONTENT
              ================================================== */}

              <div
                className="
                  mt-auto
                  flex
                  flex-col
                  items-start
                  gap-3
                  pt-5
                  sm:flex-row
                  sm:items-center
                  sm:justify-between
                  sm:pt-6
                "
              >
                {/* Trust statement */}

                <div
                  className="
                    flex
                    items-center
                    gap-1.5
                  "
                >
                  <span
                    className="
                      flex
                      h-5
                      w-5
                      items-center
                      justify-center
                      rounded-full
                      bg-[var(--brand-primary)]/[0.1]
                      brand-color
                    "
                  >
                    <Check
                      size={10}
                      strokeWidth={2.5}
                    />
                  </span>

                  <span
                    className="
                      font-saira
                      text-[7.5px]
                      font-medium
                      text-white/45
                      sm:text-[8.5px]
                    "
                  >
                    Practical solutions. Meaningful results.
                  </span>
                </div>

                {/* CTA */}

                <button
                  type="button"
                  onClick={handleLearnMore}
                  className="
                    group
                    inline-flex
                    items-center
                    gap-1.5
                    rounded-full
                    border
                    border-white/[0.12]
                    bg-white/[0.04]
                    px-3
                    py-1.5
                    font-saira
                    text-[8.5px]
                    font-semibold
                    text-white
                    backdrop-blur-md
                    transition-all
                    duration-300
                    hover:border-[var(--brand-primary)]/40
                    hover:bg-[var(--brand-primary)]
                    hover:text-black
                    sm:px-4
                    sm:py-2
                    sm:text-[9.5px]
                  "
                >
                  Discover more

                  <span
                    className="
                      flex
                      h-4
                      w-4
                      items-center
                      justify-center
                      rounded-full
                      bg-white/[0.08]
                      transition-all
                      duration-300
                      group-hover:rotate-45
                      group-hover:bg-black
                      group-hover:text-white
                      sm:h-5
                      sm:w-5
                    "
                  >
                    <ArrowUpRight
                      size={9}
                      strokeWidth={2}
                    />
                  </span>
                </button>
              </div>
            </div>

            {/* ==================================================
                CARD BOTTOM ACCENT
            =================================================== */}

            <div
              className="
                pointer-events-none
                absolute
                bottom-0
                left-0
                h-px
                w-full
                bg-gradient-to-r
                from-transparent
                via-[var(--brand-primary)]/50
                to-transparent
                opacity-60
              "
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Solution;